import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import JsonLd from "../../components/seo/json-ld";
import InsightArticle from "../../components/insights/insight-article";
import {
  INSIGHTS_SITE_URL,
  getPublicInsightBySlug,
  getPublicInsights,
} from "../../services/insights-admin-service";
import { formatInsightDate, joinKeywords } from "../../components/insights/insight-utils";
import "../../assets/css/insights.css";

const buildPostSchemas = (post) => [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${INSIGHTS_SITE_URL}/#organization`,
    name: "Avani Cimcon Technologies",
    url: INSIGHTS_SITE_URL,
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${INSIGHTS_SITE_URL}/#website`,
    name: "Avani Cimcon Technologies",
    url: INSIGHTS_SITE_URL,
    publisher: { "@id": `${INSIGHTS_SITE_URL}/#organization` },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: INSIGHTS_SITE_URL },
      { "@type": "ListItem", position: 2, name: "Insights & Perspectives", item: `${INSIGHTS_SITE_URL}/resources/insights` },
      { "@type": "ListItem", position: 3, name: post.title, item: post.canonicalUrl },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${post.canonicalUrl}#blogposting`,
    mainEntityOfPage: post.canonicalUrl,
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    datePublished: post.publishDate,
    dateModified: post.updatedDate || post.publishDate,
    articleSection: post.category,
    keywords: joinKeywords(post.tags || []),
    image: [post.ogImage || post.heroImage].filter(Boolean),
    author: {
      "@type": "Organization",
      name: post.authorName || "Avani Cimcon Technologies",
      url: INSIGHTS_SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "Avani Cimcon Technologies",
      url: INSIGHTS_SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${INSIGHTS_SITE_URL}/images/logo.png`,
      },
    },
  },
];

const InsightDetail = () => {
  const { slug } = useParams();
  const post = getPublicInsightBySlug(slug);
  const allPublished = getPublicInsights();

  const relatedInsights = useMemo(() => {
    if (!post) return [];
    const pool = allPublished.filter((item) => item.slug !== post.slug);
    const matches = pool.filter((item) => item.category === post.category || (item.tags || []).some((tag) => (post.tags || []).includes(tag)));
    return (matches.length ? matches : pool).slice(0, 3);
  }, [post, allPublished]);

  if (!post) {
    return (
      <main className="insights-page">
        <section className="insights-hero">
          <div className="container">
            <p className="insights-kicker">Insights</p>
            <h1>Insight not found</h1>
            <p>
              The requested article is not available in the current published catalog.
            </p>
            <Link to="/resources/insights" className="hero-v2-btn-primary">
              Back to Insights &amp; Perspectives
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const title = post.metaTitle || post.title;
  const description = post.metaDescription || post.excerpt;

  return (
    <React.Fragment>
      <Helmet defer={false}>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={joinKeywords(post.tags || [])} />
        <link rel="canonical" href={post.canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={post.canonicalUrl} />
        <meta property="og:title" content={post.ogTitle || post.title} />
        <meta property="og:description" content={post.ogDescription || description} />
        <meta property="og:image" content={post.ogImage || `${INSIGHTS_SITE_URL}/images/og-homepage.jpg`} />
        <meta property="og:site_name" content="Avani Cimcon Technologies" />
        <meta property="article:published_time" content={post.publishDate} />
        {post.updatedDate ? <meta property="article:modified_time" content={post.updatedDate} /> : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.twitterTitle || post.title} />
        <meta name="twitter:description" content={post.twitterDescription || description} />
        <meta name="twitter:image" content={post.twitterImage || post.ogImage || `${INSIGHTS_SITE_URL}/images/og-homepage.jpg`} />
      </Helmet>
      <JsonLd data={buildPostSchemas(post)} />

      <main className="insights-page">
        <section className="insights-hero" style={{ paddingBottom: "36px" }}>
          <div className="container">
            <p className="insights-kicker">Insights / Blog</p>
            <div className="insights-meta-row">
              <Link to="/resources/insights" className="text-white-50">
                Insights &amp; Perspectives
              </Link>
              <span>·</span>
              <span>{post.category}</span>
              <span>·</span>
              <span>{formatInsightDate(post.publishDate)}</span>
            </div>
          </div>
        </section>

        <section className="insights-section" style={{ paddingTop: "36px" }}>
          <div className="container">
            <InsightArticle post={post} relatedInsights={relatedInsights} />
          </div>
        </section>
      </main>
    </React.Fragment>
  );
};

export default InsightDetail;

